import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LucideIcon, UserRound, ChevronDown, Lock, LogOut, Menu, X, Brain, Leaf, Bone, Baby, Stethoscope, Heart, Eye, Ear, Smile, Activity, Sparkles, BookOpen, Pill, Hand, HeartPulse } from 'lucide-react';
import ciuLogo from '../assets/ciu-logo.svg';
import { useAuth } from '../contexts/AuthContext';
import UserProfileEditor from '../components/UserProfileEditor';
import './v2.css';

const ICONS: Record<string, LucideIcon> = {
  brain: Brain, leaf: Leaf, bone: Bone, baby: Baby, stethoscope: Stethoscope, heart: Heart, eye: Eye,
  ear: Ear, mouth: Smile, tooth: Smile, star: BookOpen, pill: Pill, hand: Hand, lungs: Activity,
  thermometer: HeartPulse, flower: Sparkles,
};

const WA = (process.env.REACT_APP_WHATSAPP || '5491136885204').replace(/\D/g, '');
export const ADDRESS = { line: 'Díaz Colodrero 2349', area: 'Villa Urquiza, CABA', map: 'https://www.google.com/maps/search/?api=1&query=D%C3%ADaz+Colodrero+2349%2C+Villa+Urquiza%2C+Buenos+Aires', embed: 'https://maps.google.com/maps?q=D%C3%ADaz+Colodrero+2349%2C+Villa+Urquiza%2C+Buenos+Aires&z=16&output=embed' };
export const WHATSAPP_DISPLAY = '+54 9 11 3688-5204';
export const WHATSAPP_URL = `https://wa.me/${WA}`;
export const CONTACT_EMAIL = 'info@consultoriosintegralesurquiza.com.ar';
/** WhatsApp link only when a real number is configured (REACT_APP_WHATSAPP); otherwise email. */
export const contactLink = WA
  ? { href: `https://wa.me/${WA}`, label: 'WhatsApp', external: true }
  : { href: `mailto:${CONTACT_EMAIL}`, label: 'email', external: false };

export const SpecialtyIcon: React.FC<{ name?: string; size?: number }> = ({ name, size = 26 }) => {
  const Icon = ICONS[name || ''] || Stethoscope;
  return <Icon size={size} strokeWidth={1.8} />;
};

export const initials = (name: string) =>
  name.replace(/^(Dra?\.|Lic\.)\s*/i, '').split(' ').slice(0, 2).map(s => s[0]).join('').toUpperCase();

export const Nav: React.FC = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);     // account dropdown (desktop)
  const [sheet, setSheet] = useState(false);   // mobile menu
  const [editing, setEditing] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  useEffect(() => { setSheet(false); setMenu(false); }, [pathname]);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenu(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const role = user?.role;
  const mine = role === 'admin' ? { to: '/admin', label: 'Administración' }
    : role === 'professional' ? { to: '/dashboard', label: 'Mi panel' }
    : { to: '/my-appointments', label: 'Mis turnos' };
  const roleLabel = role === 'admin' ? 'Administrador' : role === 'professional' ? 'Profesional' : 'Paciente';
  const signOut = () => { logout(); setMenu(false); setSheet(false); navigate('/'); };

  const avatar = user?.profile_picture
    ? <img src={user.profile_picture} alt="" className="v2-av" />
    : <span className="v2-av">{(user?.name || 'U').charAt(0).toUpperCase()}</span>;

  return (
    <header className="v2-nav">
      <div className="wrap row">
        <Link to="/" className="v2-brand" aria-label="Consultorios Integrales Urquiza, inicio">
          <img src={ciuLogo} alt="" />
          <span>Consultorios Integrales Urquiza<small>Salud integral</small></span>
        </Link>

        <nav className="v2-links" aria-label="Principal">
          <Link to="/#especialidades" className="hide-m">Especialidades</Link>
          <Link to="/#como-funciona" className="hide-m">Cómo funciona</Link>
          <Link to="/#contacto" className="hide-m">Contacto</Link>
          {user ? (<>
            <Link to={mine.to} className="hide-m">{mine.label}</Link>
            <div className="v2-acc hide-m" ref={menuRef}>
              <button className="v2-acc-btn" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>
                {avatar}<ChevronDown size={16} />
              </button>
              {menu && (
                <div className="v2-drop" role="menu">
                  <div className="who"><b>{user.name}</b><span>{user.email}</span><em>{roleLabel}</em></div>
                  <button role="menuitem" onClick={() => { setEditing(true); setMenu(false); }}><UserRound size={16} /> Editar perfil</button>
                  <button role="menuitem" onClick={() => { setEditing(true); setMenu(false); }}><Lock size={16} /> Cambiar contraseña</button>
                  <button role="menuitem" className="danger" onClick={signOut}><LogOut size={16} /> Cerrar sesión</button>
                </div>
              )}
            </div>
          </>) : (<>
            <Link to="/login" className="hide-m">Ingresar</Link>
            <Link to="/#especialidades" className="btn2 btn2-cta btn2-sm">Pedir turno</Link>
          </>)}
          <button className="v2-burger" aria-label={sheet ? 'Cerrar menú' : 'Abrir menú'} aria-expanded={sheet} onClick={() => setSheet(!sheet)}>
            {sheet ? <X size={22} /> : <Menu size={22} />}
          </button>
        </nav>
      </div>

      {sheet && (
        <div className="v2-sheet">
          <div className="wrap">
            <Link to="/#especialidades">Especialidades</Link>
            <Link to="/#como-funciona">Cómo funciona</Link>
            <Link to="/#contacto">Contacto</Link>
            {user ? (<>
              <Link to={mine.to}>{mine.label}</Link>
              <button onClick={() => { setEditing(true); setSheet(false); }}>Editar perfil y contraseña</button>
              <button className="danger" onClick={signOut}>Cerrar sesión ({user.name.split(' ')[0]})</button>
            </>) : (<>
              <Link to="/#especialidades" style={{ color: 'var(--o-600)' }}>Pedir turno</Link>
              <Link to="/login">Ingresar</Link>
              <Link to="/register">Crear cuenta de paciente</Link>
            </>)}
          </div>
        </div>
      )}

      {editing && user && (
        <UserProfileEditor user={user} onUpdate={(u: any) => { updateUser(u); setEditing(false); }} onClose={() => setEditing(false)} />
      )}
    </header>
  );
};

export const Footer: React.FC<{ specialties?: Array<{ id: number; name: string }> | null }> = ({ specialties }) => (
  <footer className="v2-foot">
    <div className="wrap v2-foot-grid">
      <div className="v2-foot-brand">
        <Link to="/" className="v2-foot-logo" aria-label="Consultorios Integrales Urquiza, inicio">
          <img src={ciuLogo} alt="" width={40} height={40} />
          <span>Consultorios Integrales Urquiza</span>
        </Link>
        <p>Salud integral en un mismo lugar. Elegí tu especialidad y pedí tu turno online en un minuto.</p>
        <Link to="/#especialidades" className="btn2 btn2-cta btn2-sm">Pedir un turno</Link>
      </div>

      {specialties && specialties.length > 0 && (
        <nav aria-label="Especialidades">
          <h4>Especialidades</h4>
          <ul>{specialties.map(sp => <li key={sp.id}><Link to={`/reservar/${sp.id}`}>{sp.name}</Link></li>)}</ul>
        </nav>
      )}

      <div>
        <h4>Atención</h4>
        <ul>
          <li><a href={ADDRESS.map} target="_blank" rel="noreferrer">{ADDRESS.line}<br />{ADDRESS.area} · Ver mapa</a></li>
          <li>Lunes a sábados</li>
          <li>Respondemos en menos de 24 horas hábiles</li>
          <li><a href={contactLink.href} {...(contactLink.external ? { target: '_blank', rel: 'noreferrer' } : {})}>{contactLink.external ? `WhatsApp ${WHATSAPP_DISPLAY}` : CONTACT_EMAIL}</a></li>
          {contactLink.external && <li><a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></li>}
        </ul>
      </div>

      <nav aria-label="Tu cuenta">
        <h4>Tu cuenta</h4>
        <ul>
          <li><Link to="/login">Ingresar</Link></li>
          <li><Link to="/register">Crear cuenta de paciente</Link></li>
          <li><Link to="/#como-funciona">Cómo funciona</Link></li>
          <li><Link to="/#contacto">Contacto</Link></li>
          <li><Link to="/preguntas-frecuentes">Preguntas frecuentes</Link></li>
        </ul>
      </nav>
    </div>
    <div className="wrap v2-foot-base">
      <span>© {new Date().getFullYear()} Consultorios Integrales Urquiza · Profesionales matriculados</span>
      <a href="#top" onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Volver arriba ↑</a>
    </div>
  </footer>
);


export const V2: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className="v2">{children}</div>;

/** Purple page header used by dashboards. Wrapped so it works outside the .v2 page shell. */
export const PageBand: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <div className="v2 v2-bare">
    <section className="pg"><div className="wrap">
      <h1>{title}</h1>
      {subtitle && <p>{subtitle}</p>}
    </div></section>
  </div>
);

/** Sets the browser tab title (and restores the default on leave). */
export const usePageTitle = (title?: string) => {
  React.useEffect(() => {
    const base = 'Consultorios Integrales Urquiza';
    document.title = title ? `${title} · ${base}` : base;
    return () => { document.title = base; };
  }, [title]);
};
